// Building blocks shared by the /book-a-demo card and <BookingWidget />.
import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './styles';
import { cx } from '../ui';
import {
  type AvailDate,
  type BookingResult,
  type Slot,
  addDays,
  CONTACT_EMAIL,
  fmtDay,
  fmtTime,
  fmtWhen,
  googleCalendarUrl,
  sendProduct,
  todayIn,
  tzAbbr,
  tzCity,
  tzOffset,
  tzOptions,
  tzLabel,
  ymdToDate,
} from './useBooking';

// ---------- icons ----------
export const IconCheck = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="m3.5 8.5 3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
export const IconAlert = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.6" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 4.8v3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="8" cy="11" r="1" fill="currentColor" />
  </svg>
);
export const IconArrow = ({ back = false }: { back?: boolean }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" style={back ? { transform: 'scaleX(-1)' } : undefined}>
    <path d="M3 8h9.5M8.5 3.5 13 8l-4.5 4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IconCal = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="2" y="3" width="12" height="11" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M2 6.5h12M5.5 1.6v2.6M10.5 1.6v2.6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IconMail = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.8" y="3.2" width="12.4" height="9.6" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="m2.5 4.5 5.5 4 5.5-4" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const IconVideo = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <rect x="1.6" y="4" width="9" height="8" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="m10.6 7 3.8-2.2v6.4L10.6 9" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);
const IconGlobe = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.5" />
    <path d="M1.8 8h12.4M8 1.6c1.8 1.9 2.6 4 2.6 6.4S9.8 12.5 8 14.4C6.2 12.5 5.4 10.4 5.4 8S6.2 3.5 8 1.6Z" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

export const Spinner = () => <span className="bk-spin" aria-hidden="true" />;

// ---------- animated height container ----------
export function AutoHeight({ children, className }: { children: React.ReactNode; className?: string }) {
  const inner = useRef<HTMLDivElement>(null);
  const [h, setH] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = inner.current;
    if (!el) return;
    const measure = () => setH(el.offsetHeight);
    measure();
    if (!('ResizeObserver' in window)) return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div className={cx('bk-autoh', className)} style={{ height: h }}>
      <div ref={inner}>{children}</div>
    </div>
  );
}

// ---------- fields ----------
type TextFieldProps = {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  optional?: boolean;
  note?: React.ReactNode;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>;

export function TextField({ label, value, onChange, error, optional, note, className, inputRef, ...rest }: TextFieldProps) {
  const id = useId();
  const errId = `${id}-err`;
  return (
    <div className={className}>
      <label htmlFor={id} className="bk-label">
        {label} {optional && <span className="opt">(optional)</span>}
      </label>
      <input
        id={id}
        ref={inputRef}
        className="bk-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errId : undefined}
        {...rest}
      />
      {error && (
        <div id={errId} className="bk-err" role="alert">
          <IconAlert />
          <span>{error}</span>
        </div>
      )}
      {!error && note}
    </div>
  );
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  placeholder = 'Choose one',
  optional,
  className,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  optional?: boolean;
  className?: string;
}) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="bk-label">
        {label} {optional && <span className="opt">(optional)</span>}
      </label>
      <select id={id} className="bk-input" value={value} onChange={(e) => onChange(e.target.value)} style={{ color: value ? undefined : 'var(--bk-muted)' }}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

/** Chip group. multi = toggle buttons; single = radiogroup (click again to clear). */
export function Chips({
  label,
  options,
  value,
  onChange,
  multi = false,
  optional,
}: {
  label: string;
  options: { key: string; label: string }[];
  value: string | string[];
  onChange: (v: any) => void;
  multi?: boolean;
  optional?: boolean;
}) {
  const id = useId();
  const sel = Array.isArray(value) ? value : value ? [value] : [];
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const toggle = (k: string) => {
    if (multi) onChange(sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]);
    else onChange(sel[0] === k ? '' : k);
  };
  const onKey = (e: React.KeyboardEvent, i: number) => {
    if (multi) return;
    const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const n = (i + d + options.length) % options.length;
    refs.current[n]?.focus();
    onChange(options[n].key);
  };
  const focusIdx = multi ? -1 : Math.max(0, options.findIndex((o) => o.key === sel[0]));
  return (
    <div>
      <div id={id} className="bk-label">
        {label} {optional && <span className="opt">(optional)</span>}
      </div>
      <div role={multi ? 'group' : 'radiogroup'} aria-labelledby={id} className="flex flex-wrap gap-2">
        {options.map((o, i) => {
          const on = sel.includes(o.key);
          return (
            <button
              key={o.key}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              className="bk-chip"
              role={multi ? undefined : 'radio'}
              aria-pressed={multi ? on : undefined}
              aria-checked={multi ? undefined : on}
              tabIndex={multi || i === focusIdx ? 0 : -1}
              onClick={() => toggle(o.key)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {multi && on && (
                <span className="tick">
                  <IconCheck />
                </span>
              )}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- progress ----------
export function Progress({ step, labels, onJump }: { step: number; labels: string[]; onJump: (i: number) => void }) {
  return (
    <nav aria-label="Booking progress">
      <ol className="bk-prog" style={{ gridTemplateColumns: `repeat(${labels.length}, 1fr)` }}>
        {labels.map((l, i) => (
          <li key={l}>
            <button
              type="button"
              className={cx(i <= step && 'on', i === step && 'cur', 'w-full')}
              disabled={i >= step}
              onClick={() => onJump(i)}
              aria-current={i === step ? 'step' : undefined}
            >
              <span className="track">
                <span className="fill" />
              </span>
              <span className="lbl">
                <span className="sr-only">Step {i + 1} of {labels.length}: </span>
                {l}
              </span>
            </button>
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ---------- time zone line ----------
export function TzLine({ tz, detected, onChange, className }: { tz: string; detected: string; onChange: (tz: string) => void; className?: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const selRef = useRef<HTMLSelectElement>(null);
  useEffect(() => {
    if (open) selRef.current?.focus();
  }, [open]);
  return (
    <div className={cx('flex flex-wrap items-center gap-x-2 gap-y-2 text-[13.5px] bk-muted', className)}>
      <span className="inline-flex items-center gap-1.5">
        <IconGlobe />
        {open ? (
          <label htmlFor={id}>Time zone</label>
        ) : (
          <span>
            Times in <strong className="font-semibold" style={{ color: 'var(--bk-fg)' }}>{tzCity(tz)}</strong> time ({tzOffset(tz)})
          </span>
        )}
      </span>
      {open ? (
        <select
          id={id}
          ref={selRef}
          className="bk-input"
          style={{ height: 40, width: 'auto', fontSize: 16, paddingLeft: 12 }}
          value={tz}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(false);
          }}
          onBlur={() => setOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        >
          {tzOptions(detected).map((z) => (
            <option key={z} value={z}>
              {tzLabel(z)} ({tzOffset(z)}){z === detected ? ', detected' : ''}
            </option>
          ))}
        </select>
      ) : (
        <button type="button" className="bk-link text-[13.5px]" onClick={() => setOpen(true)} aria-label={`Change time zone, currently ${tzCity(tz)}`}>
          Change
        </button>
      )}
    </div>
  );
}

// ---------- month-ish grid ----------
const WD = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const weekdayIdx = (ymd: string) => (ymdToDate(ymd).getUTCDay() + 6) % 7;
const monthName = (ymd: string, short = false) => ymdToDate(ymd).toLocaleString('en-GB', { month: short ? 'short' : 'long', timeZone: 'UTC' });

export function DateGrid({
  from,
  days,
  dates,
  selected,
  onSelect,
  tz,
}: {
  from: string;
  days: number;
  dates: AvailDate[];
  selected: string | null;
  onSelect: (d: string) => void;
  tz: string;
}) {
  const avail = useMemo(() => new Set(dates.map((d) => d.date)), [dates]);
  const today = todayIn(tz);
  const start = addDays(from, -weekdayIdx(from));
  const last = addDays(from, days - 1);
  const end = addDays(last, 6 - weekdayIdx(last));
  const cells: string[] = [];
  for (let d = start; d <= end; d = addDays(d, 1)) cells.push(d);
  const rows: string[][] = [];
  for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
  const m1 = monthName(from);
  const m2 = monthName(last);
  const year = ymdToDate(last).getUTCFullYear();
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const focusable = selected && avail.has(selected) ? selected : dates[0]?.date;

  const move = (cur: string, delta: number) => {
    // jump to the nearest available day in the direction of travel
    let d = addDays(cur, delta);
    const step = delta > 0 ? 1 : -1;
    for (let guard = 0; guard < 40 && d >= start && d <= end; guard++) {
      if (avail.has(d)) return d;
      d = addDays(d, Math.abs(delta) === 7 ? delta : step);
    }
    return null;
  };
  const onKey = (e: React.KeyboardEvent, d: string) => {
    const map: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: 7, ArrowUp: -7 };
    let next: string | null = null;
    if (map[e.key]) next = move(d, map[e.key]);
    else if (e.key === 'Home') next = dates[0]?.date ?? null;
    else if (e.key === 'End') next = dates[dates.length - 1]?.date ?? null;
    else return;
    e.preventDefault();
    if (next) {
      onSelect(next);
      requestAnimationFrame(() => refs.current[next!]?.focus());
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between">
        <div className="bk-h text-[16px]">{m1 === m2 ? `${m1} ${year}` : `${monthName(from, true)} to ${monthName(last, true)} ${year}`}</div>
        <div className="text-[12.5px] bk-muted">{dates.length} days open</div>
      </div>
      <div role="grid" aria-label="Choose a date" className="bk-cal">
        <div role="row" className="bk-cal-head">
          {WD.map((w) => (
            <span role="columnheader" key={w} aria-label={w}>
              {w.slice(0, 2)}
            </span>
          ))}
        </div>
        {rows.map((r) => (
          <div role="row" className="bk-cal-row gap-1" key={r[0]}>
            {r.map((d) => {
              const ok = avail.has(d);
              const dn = ymdToDate(d).getUTCDate();
              return (
                <div role="gridcell" key={d} aria-selected={ok ? selected === d : undefined}>
                  <button
                    type="button"
                    ref={(el) => {
                      refs.current[d] = el;
                    }}
                    className={cx('bk-day', ok && 'avail', d === today && 'today')}
                    aria-selected={selected === d}
                    disabled={!ok}
                    tabIndex={d === focusable ? 0 : -1}
                    onClick={() => onSelect(d)}
                    onKeyDown={(e) => onKey(e, d)}
                    aria-label={`${ymdToDate(d).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}${ok ? '' : ', no times'}`}
                  >
                    {dn}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- horizontal date strip (compact) ----------
export function DateStrip({ dates, selected, onSelect }: { dates: AvailDate[]; selected: string | null; onSelect: (d: string) => void }) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: React.KeyboardEvent, i: number) => {
    const d = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
    if (!d) return;
    e.preventDefault();
    const n = Math.min(dates.length - 1, Math.max(0, i + d));
    onSelect(dates[n].date);
    refs.current[n]?.focus();
  };
  const stripRef = useRef<HTMLDivElement>(null);
  // keep the selected day visible by scrolling the strip only (never the page)
  useEffect(() => {
    const strip = stripRef.current;
    const el = refs.current[dates.findIndex((d) => d.date === selected)];
    if (!strip || !el) return;
    const l = el.offsetLeft - strip.offsetLeft;
    if (l < strip.scrollLeft) strip.scrollLeft = Math.max(0, l - 2);
    else if (l + el.offsetWidth > strip.scrollLeft + strip.clientWidth - 28) strip.scrollLeft = l + el.offsetWidth - strip.clientWidth + 28;
  }, [selected, dates]);
  return (
    <div ref={stripRef} className="bk-strip" role="group" aria-label="Choose a date">
      {dates.map((d, i) => {
        const dt = ymdToDate(d.date);
        return (
          <button
            key={d.date}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            className="bk-sday"
            aria-pressed={selected === d.date}
            tabIndex={selected === d.date || (!selected && i === 0) ? 0 : -1}
            onClick={() => onSelect(d.date)}
            onKeyDown={(e) => onKey(e, i)}
            aria-label={`${dt.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' })}, ${d.slots.length} times`}
          >
            <span className="wd">{d.weekday}</span>
            <span className="dn">{dt.getUTCDate()}</span>
            <span className="mo">{monthName(d.date, true)}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---------- time slots ----------
export function TimeGrid({
  slots,
  tz,
  selected,
  onSelect,
  label,
  limit,
  cols4,
}: {
  slots: Slot[];
  tz: string;
  selected: string | null;
  onSelect: (s: string) => void;
  label: string;
  limit?: number;
  cols4?: boolean;
}) {
  const [all, setAll] = useState(false);
  const shown = limit && !all ? slots.slice(0, limit) : slots;
  return (
    <div>
      <div className={cx('bk-times', cols4 && 'bk-times-4')} role="group" aria-label={label}>
        {shown.map((s) => (
          <button key={s.start} type="button" className="bk-time" aria-pressed={selected === s.start} onClick={() => onSelect(s.start)}>
            {fmtTime(s.start, tz)}
          </button>
        ))}
      </div>
      {limit && !all && slots.length > limit && (
        <button type="button" className="bk-link mt-3 text-[14px]" onClick={() => setAll(true)}>
          Show {slots.length - limit} more times
        </button>
      )}
    </div>
  );
}

export function PickerSkeleton({ compact = false }: { compact?: boolean }) {
  if (compact)
    return (
      <div aria-hidden="true">
        <div className="flex gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bk-skel" style={{ width: 64, height: 74, borderRadius: 14 }} />
          ))}
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bk-skel" style={{ height: 46 }} />
          ))}
        </div>
      </div>
    );
  return (
    <div className="bk-pick-grid" aria-hidden="true">
      <div>
        <div className="bk-skel mb-4" style={{ width: 150, height: 18 }} />
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 28 }).map((_, i) => (
            <div key={i} className="bk-skel" style={{ aspectRatio: '1', maxHeight: 48, opacity: i % 7 > 4 ? 0.4 : 1 }} />
          ))}
        </div>
      </div>
      <div className="grid gap-2 content-start">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bk-skel" style={{ height: 46 }} />
        ))}
      </div>
    </div>
  );
}

export function NoSlots() {
  return (
    <div className="rounded-2xl p-6 text-center" style={{ background: 'var(--bk-soft)' }}>
      <div className="bk-h text-[18px]">No slots in the next 3 weeks.</div>
      <p className="mt-2 text-[15px] bk-muted">We are fully booked for now. Email us and we will find a time that works.</p>
      <a className="bk-ghost mt-4" href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('A time for 30 minutes')}`}>
        <IconMail /> {CONTACT_EMAIL}
      </a>
    </div>
  );
}

export function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div className="bk-alert" role="alert">
      <IconAlert />
      <div>{children}</div>
    </div>
  );
}

/** Hidden honeypot. Humans never see or fill it. */
export function Honeypot({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="bk-hp" aria-hidden="true">
      <label>
        Website
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
      </label>
    </div>
  );
}

// ---------- product capture ----------
/** After booking: one optional product, so we can prepare the demo around it. */
function ProductPanel({ token, initial }: { token: string; initial?: string }) {
  const [saved, setSaved] = useState(initial || '');
  const [value, setValue] = useState('');
  const [editing, setEditing] = useState(!initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return setError('Add a product name or a link');
    setBusy(true);
    setError(null);
    try {
      await sendProduct(token, v);
      setSaved(v);
      setEditing(false);
    } catch (x) {
      setError((x as Error).message || 'That did not send. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bk-panel">
      <div className="bk-h text-[16px]">Make the demo about your product</div>
      {!editing ? (
        <p className="mt-1.5 text-[14.5px] leading-snug bk-muted">
          We'll prepare with <strong style={{ color: 'var(--bk-fg)' }}>{saved}</strong>.{' '}
          <button
            type="button"
            className="bk-link"
            onClick={() => {
              setValue(saved);
              setEditing(true);
            }}
          >
            Change
          </button>
        </p>
      ) : (
        <form className="mt-3" onSubmit={save} noValidate>
          <label className="sr-only" htmlFor="bk-product">
            Product name or link
          </label>
          <div className="flex gap-2 max-sm:flex-col">
            <input
              id="bk-product"
              className="bk-input min-w-0 flex-1"
              placeholder="A model name or a link to its page"
              maxLength={300}
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              aria-invalid={error ? true : undefined}
            />
            <button type="submit" className="bk-ghost justify-center" aria-disabled={busy} disabled={busy}>
              {busy ? <Spinner /> : null} Send
            </button>
          </div>
          {error && (
            <div className="bk-err" role="alert">
              <IconAlert />
              <span>{error}</span>
            </div>
          )}
        </form>
      )}
      <p className="mt-3 text-[14px] leading-snug bk-muted">If you can, bring a photo of its serial number or product label. We'll take it from there.</p>
    </div>
  );
}

// ---------- confirmation ----------
export function Confirmation({
  result,
  tz,
  product,
  cancelled,
  onCancel,
  onRebook,
  compact = false,
}: {
  result: BookingResult;
  tz: string;
  product?: string;
  cancelled: boolean;
  onCancel: () => Promise<void>;
  onRebook: () => void;
  compact?: boolean;
}) {
  const m = result.meeting;
  const when = fmtWhen(m.start, tz);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const headRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headRef.current?.focus();
  }, [cancelled]);

  const site = typeof window !== 'undefined' ? `${window.location.origin}/` : 'https://www.brandedmark.com/';
  const mailBody = [
    'Hi,',
    '',
    `I have booked 30 minutes with BrandedMark on ${fmtDay(m.start, tz, true)} at ${fmtTime(m.start, tz)} (${tzCity(tz)} time).`,
    product ? `We will look at one of our products together: ${product}.` : 'We will look at one of our products together.',
    '',
    ...(result.meetingLink ? [`Teams link: ${result.meetingLink}`, ''] : []),
    `Have a look first: ${site}`,
    '',
    result.meetingLink ? 'Join with the Teams link, or reply and I will forward the invite.' : 'Want to join? Reply and I will forward the invite.',
  ].join('\n');
  const mailto = `mailto:?subject=${encodeURIComponent('Worth 30 minutes? BrandedMark call booked')}&body=${encodeURIComponent(mailBody)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.meetingLink || site);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      /* clipboard blocked */
    }
  };

  const doCancel = async () => {
    setBusy(true);
    setErr(null);
    try {
      await onCancel();
      setConfirming(false);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (cancelled)
    return (
      <div className="bk-step-in py-2 text-center">
        <h2 ref={headRef} tabIndex={-1} className="bk-h text-[24px] outline-none">
          Booking cancelled.
        </h2>
        <p className="mx-auto mt-2 max-w-sm text-[15.5px] bk-muted">
          {when} is free again. Nothing else to do. If another time suits better, pick one now.
        </p>
        <button type="button" className="bk-btn mt-6" onClick={onRebook}>
          Pick another time <IconArrow />
        </button>
      </div>
    );

  const day = new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'long' }).format(new Date(m.start));
  const range = `${fmtTime(m.start, tz)}–${fmtTime(m.end, tz)} ${tzAbbr(m.start, tz)}`;

  return (
    <div className="bk-step-in" aria-live="polite">
      <div className="flex items-center gap-3">
        <svg className="bk-tick shrink-0" width={compact ? 36 : 40} height={compact ? 36 : 40} viewBox="0 0 64 64" aria-hidden="true">
          <defs>
            <linearGradient id="bk-tick-g" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#3FAE6C" />
              <stop offset=".55" stopColor="#8FD53A" />
              <stop offset="1" stopColor="#CFF710" />
            </linearGradient>
          </defs>
          <circle cx="32" cy="32" r="32" fill="url(#bk-tick-g)" />
          <path d="m20 33 8 8 16-18" fill="none" stroke="#241E61" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <div className="bk-mono" style={{ color: 'var(--bk-accent)' }}>
          Booking confirmed
        </div>
      </div>
      <h2 ref={headRef} tabIndex={-1} className={cx('bk-h mt-4 scroll-mt-28 outline-none', compact ? 'text-[22px]' : 'text-[30px] leading-tight')}>
        See you on {day}.
      </h2>

      <div className="bk-when">
        <div className="bk-when-day">{fmtDay(m.start, tz, true)}</div>
        <div className="bk-when-time">{range}</div>
        <div className="bk-when-where">
          <IconVideo /> Microsoft Teams
          {result.meetingLink && (
            <>
              <span aria-hidden="true">·</span>
              <a className="bk-link" href={result.meetingLink} target="_blank" rel="noreferrer">
                Join link
              </a>
            </>
          )}
        </div>
      </div>

      <p className="mt-4 text-[15px] bk-muted">
        {result.invited ? "We've sent a calendar invite to" : "We'll email the invite to"} <strong style={{ color: 'var(--bk-fg)' }}>{m.email}</strong>.
      </p>
      {result.freeEmail && (
        <div className="bk-note">
          <IconAlert />
          <span>You booked with a personal address. If you can, forward the invite to your work email, it helps us prepare.</span>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3">
        <a className="bk-btn" href={result.icsUrl} download="brandedmark-demo.ics">
          <IconCal /> Add to calendar
        </a>
        <a className="bk-link text-[14.5px]" href={googleCalendarUrl(m, result.meetingLink)} target="_blank" rel="noreferrer">
          Google Calendar
        </a>
      </div>

      <ProductPanel token={result.cancelToken} initial={product} />

      {!compact && (
        <p className="mt-6 text-[14.5px] bk-muted">
          Optional: invite someone from after-sales or IT.{' '}
          <a className="bk-link" href={mailto}>
            Email them
          </a>
          {' · '}
          <button type="button" className="bk-link" onClick={copy}>
            {copied ? 'Link copied' : result.meetingLink ? 'Copy Teams link' : 'Copy link'}
          </button>
        </p>
      )}

      <div className="mt-6 text-[14px] bk-muted">
        {!confirming ? (
          <>
            Need another time?{' '}
            <button type="button" className="bk-link" onClick={() => setConfirming(true)}>
              Cancel this one
            </button>{' '}
            and pick again.
          </>
        ) : (
          <div className="rounded-xl p-4" style={{ background: 'var(--bk-soft)' }} role="group" aria-label="Confirm cancellation">
            <div className="font-semibold" style={{ color: 'var(--bk-fg)' }}>
              Cancel {when}?
            </div>
            <p className="mt-1">We let everyone know, and you can pick a new time straight away.</p>
            {err && <div className="bk-err">{err}</div>}
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className="bk-ghost" onClick={doCancel} aria-disabled={busy} disabled={busy} style={{ color: 'var(--bk-error)' }}>
                {busy ? <Spinner /> : null} Yes, cancel it
              </button>
              <button type="button" className="bk-ghost" onClick={() => setConfirming(false)} autoFocus>
                Keep my booking
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
